<!--
	@name common-structure-news
	@description Displays a list of news entries with pageable listings and a detail view
	@date 2020/03/04
	@license no license
	@copywrite Answers In Retirement Limited
-->

<!--
	@example

	<common-structure-news :items="items" :pageSize="pageSize" >
		<template v-slot:title>Your Header</template>
		<template v-slot:detail>Some content</template>
	</common-structure-news>

	[
		{
			"id": "cbad5b84-7744-42f8-bf8f-d69aadd728b5",
			"name": "Join Air Group CEO for a live Q&A",
			"headline": "Stuart will be taking your questions!",
			"story": "Join us for a live Q&A with Air Group's CEO: Stuart Wilson. Taking your questions and discussing the ongoing changes to our industry and the impacts they may have on advisers like you. We look forward to seeing you there: Thursday 30th April, 11am Register here: https://www.eventbrite.co.uk/e/air-group-stuart-wilson-in-conversation-with-later-life-advisers-registration-102983941742",
			"url": null,
			"created": "2020-04-29T10:17:22.000Z",
			"updated": "2020-04-29T10:17:22.000Z",
			"tag": [
				{
					"id": "c657b7f4-f9ad-4983-b93b-578ff60f0569",
					"name": "Air Group",
					"visible": false
				}
			]
		}
	]
-->

<template>
	<div :component="$options.name">
		<div class="d-flex mb-6">
			<div class="left flex-grow-1">
				<h2 v-if="hasTitleSlot" class="text-h5 font-weight-bold mb-2">
					<slot name="title" />
				</h2>
				<p v-if="hasDetailSlot" class="text-body-1 mb-0">
					<slot name="detail" />
				</p>
			</div>
			<div>
				<v-btn-toggle v-if="!loading && length > 1">
					<v-btn small :disabled="page === 1" @click="pageDown">
						<v-icon>mdi-chevron-left</v-icon>
					</v-btn>
					<v-btn small :disabled="page === length" @click="pageUp">
						<v-icon>mdi-chevron-right</v-icon>
					</v-btn>
				</v-btn-toggle>
			</div>
		</div>
		<div class="body">
			<div v-if="loading" class="d-flex flex-wrap ma-n3">
				<v-card v-for="i in pageSize" :key="i" class="article ma-3 d-flex flex-column flex-grow-1">
					<v-skeleton-loader type="card" />
				</v-card>
			</div>
			<div v-else-if="news.length" class="d-flex flex-wrap ma-n3">
				<v-card v-for="item in news" :key="item.id" class="article ma-3 d-flex flex-column flex-grow-1">
					<v-img class="white--text align-end flex-grow-0 grey" :aspect-ratio="16/9" :src="item.image || 'placeholder/gradient.png' | urlize" />
					<v-card-subtitle v-if="item.created" class="text-caption plain font-italic pb-0 mb-0 grey--text">
						{{ formatDate(item.created) }}
					</v-card-subtitle>
					<v-card-title v-if="item.name" class="mt-1 py-0" style="word-break: normal; line-height: 1.75rem;">
						{{ item.name | truncate(60) }}
					</v-card-title>
					<v-card-text class="text--primary mt-3">
						<div>{{ item.headline | truncate(150) }}</div>
					</v-card-text>
					<v-card-actions class="mt-auto">
						<v-btn
							class="ga--trigger ga--article-opened"
							data-ga-id="dashboard"
							:data-ga-value="item.name"
							color="primary"
							text
							@click="handleReadMore(item)"
						>
							Read More
						</v-btn>
					</v-card-actions>
				</v-card>
			</div>
		</div>
		<app-dialog-news ref="news" />
	</div>
</template>

<script>
	import { mapActions, mapGetters } from 'vuex';
	import { PopupWindow } from '@/utils';
	import DateTools from '@/utils/DateTools';
	import AppDialogNews from '@/component/app/dialog/news';

	export default {
		name: 'common-structure-news',

		components: { AppDialogNews },

		props: {
			pageSize: { type: Number, required: false, default: 6 }
		},

		data() {
			return {
				page: 1,
				loading: true
			};
		},

		computed: {
			...mapGetters('CmsNews', ['news', 'total']),

			/**
			 * @name length
			 * @description Calculate pagination length from total
			 */
			length() {
				return Math.ceil(this.total / this.pageSize);
			},

			/**
			 * @name hasTitleSlot
			 * @description does the title slot have content
			 */
			hasTitleSlot() {
				return !!this.$slots.title;
			},

			/**
			 * @name hasDetailSlot
			 * @description does the detail slot have content
			 */
			hasDetailSlot() {
				return !!this.$slots.detail;
			}
		},

		watch: {
			page() {
				this.loadNews();
			}
		},

		created() {
			this.loadNews();
		},

		methods: {
			...mapActions('CmsNews', ['loadNewsList']),

			/**
			 * @name formatDate
			 * @description Format date using DateTools
			 * @param {String} dateString
			 * @return {String} Formatted date
			 */
			formatDate(dateString) {
				return DateTools.stringToLocale(dateString, 'medium');
			},

			/**
			 * @name handleReadMore
			 * @description Open external link if available or open dialog
			 */
			handleReadMore(item) {
				window.dataLayer = window.dataLayer || [];
				window.dataLayer.push({
					event: 'articleOpened',
					articleTitle: item.name || item.headline
				});

				if (item.url) new PopupWindow().open(item.url, '_blank');
				else this.$router.push(`/news/${item.id}`);
			},

			/**
			 * @name pageDown
			 * @description Go to previous page
			 */
			pageDown() {
				this.page = this.page < 2 ? 1 : this.page - 1;
			},

			/**
			 * @name pageUp
			 * @description Go to next page
			 */
			pageUp() {
				this.page = this.page >= this.length ? this.length : this.page + 1;
			},

			/**
			 * @name loadNews
			 * @description Load news with pagination
			 */
			async loadNews() {
				this.loading = true;
				await this.loadNewsList({
					offset: (this.page - 1) * this.pageSize,
					limit: this.pageSize
				});
				this.loading = false;
			}
		}
	};
</script>

<style scoped>
	.article {
		width: 30%;
	}
</style>
